import React, { createContext, useContext, useEffect, useState } from 'react';
import { cn } from '../lib/utils';
import TailwindButton from './Tailwind/TailwindButton';
import apiService from '../services/ApiService';
import TutorialModal from './TutorialModal';

const MainTutorialStepsDetails = [
    // Steps 0-3: My Models & Preset flow
    {
        key: 'models_management',
        title: 'My Models Management',
        content: (
            <p className="text-sm text-center">
                Here is the interface to manage your models.
                <br/>
                You can create, rename, share and delete your own models.
                <br/>
                You can organize your models in folders by dragging and dropping models in folders.
            </p>
        )
    },
    {
        key: 'models_creation',
        title: 'Create a Model',
        content: (
            <p className="text-sm text-center">
                Let's start creating your first financial model.
                <br/>
                After naming your model, double-click it to open and start editing.
            </p>
        )
    },
    {
        key: 'preset_page',
        title: 'Preset Selection',
        content: (
            <p className="text-sm text-center">
                When creating a model, you can choose from a list of predefined templates —
                <br/>
                or start from scratch.
            </p>
        )
    },
    {
        key: 'preset_selection',
        title: 'Preset Selection',
        content: (
            <p className="text-sm text-center">
                Select the "Trial - Wind farm project" for the tutorial.
            </p>
        )
    },
    // Steps 4-7: Architecture & Model configuration
    {
        key: 'architecture_overview',
        title: 'Architecture Overview',
        content: (
            <p className="text-sm text-center">
                Welcome to the Architecture page — the heart of your financial model.
                <br/>
                Your model is organized into sections (Companies, Financial Debt, Indexation, etc.).
                <br/>
                Click any section card to view and edit its inputs.
            </p>
        )
    },
    {
        key: 'section_detail',
        title: 'Explore a Section',
        content: (
            <p className="text-sm text-center">
                Each section displays its input and output items in a tree structure.
                <br/>
                You can add, edit, rename, and drag-and-drop objects within a section.
            </p>
        )
    },
    {
        key: 'scenario_selector',
        title: 'Manage Scenarios',
        content: (
            <p className="text-sm text-center">
                Create and switch between multiple scenarios to evaluate different setups for your project.
            </p>
        )
    },
    {
        key: 'sensitivity_selector',
        title: 'Manage Sensitivities',
        content: (
            <p className="text-sm text-center">
                Evaluate favorable and unfavorable variations within your current scenario.
                <br/>
                The debt repayment profile remains fixed under any sensitivity.
            </p>
        )
    },
    {
        key: 'status_bar',
        title: 'Status Bar',
        content: (
            <p className="text-sm text-center">
                The status bar shows the computation state of your model
                <br/>
                (Calculating, Incomplete, Complete, or Ready to build).
                <br/>
                When collaborating, it also shows who is currently editing.
            </p>
        )
    },
    // Steps 8-9: Status bar & Sidebar navigation
    {
        key: 'sidebar_navigation',
        title: 'Sidebar Navigation',
        content: (
            <p className="text-sm text-center">
                The sidebar gives you quick access to all the major features of your model.
                <br/>
                Navigate between Architecture, Dashboard, Financial Statements, and more.
            </p>
        )
    },
    // Steps 10-16: Sidebar pages
    {
        key: 'sidebar_dashboard',
        title: 'Dashboard',
        content: (
            <p className="text-sm text-center">
                Access a high-level overview of your model's key performance indicators and annual metrics.
            </p>
        )
    },
    {
        key: 'sidebar_financial_statements',
        title: 'Financial Statements',
        content: (
            <p className="text-sm text-center">
                View P&L, Balance Sheet, Cash Flow Statements, and Uses & Sources
                <br/>
                generated from your model's computed results.
            </p>
        )
    },
        {
        key: 'sidebar_compare',
        title: 'Compare Scenarios',
        content: (
            <p className="text-sm text-center">
                Compare different scenarios to identify the best option for your project.
            </p>
        )
    },
    {
        key: 'sidebar_calculation_results',
        title: 'Calculation results',
        content: (
            <p className="text-sm text-center">
                Browse the detailed computation outputs of your model, including all intermediate and final calculated values for each section.
            </p>
        )
    },
    {
        key: 'sidebar_audit',
        title: 'Audit',
        content: (
            <p className="text-sm text-center">
                Verify result consistency (e.g., balanced balance sheet or uses/sources).
            </p>
        )
    },
    {
        key: 'sidebar_history',
        title: 'History',
        content: (
            <p className="text-sm text-center">
                Track all user actions and changes for better collaboration and oversight.
            </p>
        )
    },
    {
        key: 'sidebar_tools',
        title: 'Tools & Reports',
        content: (
            <p className="text-sm text-center">
                Download your model to Excel, generate reports (Assumptions Booklet, Terms),
                <br/>
                and access additional tools from this page.
            </p>
        )
    },
    {
        key: 'sidebar_ai',
        title: 'AI Assistant',
        content: (
            <p className="text-sm text-center">
                Use the AI assistant to create your models, understand your scenarios, get specific insights
                <br/>
                and speed up your financial analysis.
            </p>
        )
    },
    {
        key: 'enjoy',
        title: 'Tutorial Finished !',
        content: (
            <p className="text-sm text-center">
                We hope you found this tutorial helpful.
                <br/>
                If you have any questions, feel free to reach out.
                <br/>
                Thank you for using our application — we encourage you to continue exploring and creating.
            </p>
        )
    }
]

const TutorialsReferences = {
    'main': MainTutorialStepsDetails,
}

// Migrate old tutorial step indices (25-step system) to new ones (18-step system)
function migrateStepIndex(oldIndex: number): number {
    // Steps 0-3 (MyModels/Preset flow) are unchanged
    if (oldIndex <= 3) return oldIndex;
    // Old steps 4-13 (Preset selection + FME detail: editor, scenarios, inputs, objects, navigation)
    // → Restart from architecture overview (new step 4)
    if (oldIndex <= 13) return 4;
    // Old steps 14-22 (output, search, helper, download, compute, dashboard, history, audit, reports)
    // → Restart from sidebar navigation (new step 9)
    if (oldIndex <= 22) return 9;
    // Old step 23 (enjoy) or beyond → tutorial was complete
    return TutorialsReferences['main'].length;
}

const TutorialContext = createContext({
    currentTutorialSteps: {},
    tutorialEnabled: false,
    nextTutorialStep: (_tutorial) => {},
    previousTutorialStep: (_tutorial) => {},
    enableTutorials: () => {},
    restartTutorial: () => {},
});

const InitTutorialSteps = {
    'main': 0
}

function TutorialProvider({ children }) {
    const [currentTutorialSteps, setCurrentTutorialSteps] = useState(InitTutorialSteps);
    const [tutorialEnabled, setTutorialEnabled] = useState(false);

    const [tutorialModalOpen, setTutorialModalOpen] = useState(false);

    const [mounted, setMounted] = useState(false)

    const previousTutorialStep = (tutorial) => {
        currentTutorialSteps[tutorial] -= 1
        setMounted(!mounted);
    }

    const nextTutorialStep = (tutorial) => {
        currentTutorialSteps[tutorial] += 1

        // Check if tutorial is now complete
        const isComplete = currentTutorialSteps[tutorial] >= TutorialsReferences[tutorial].length;

        apiService.updateTutorialAdvancement(currentTutorialSteps).then(async(response) => {
            if (response) {
                if (isComplete) {
                    // Tutorial is over, disable it
                    apiService.disableTutorial().then(async(disableResponse) => {
                        if (disableResponse) {
                            setTutorialEnabled(false);
                        }
                    });
                }
                setMounted(!mounted);
            }
        })
    }

    const enableTutorials = () => {
        setTutorialEnabled(true);
    }

    const restartTutorial = () => {
        setCurrentTutorialSteps({...InitTutorialSteps});
        setTutorialModalOpen(true);
    }

    useEffect(() => {
        apiService.getCurrentUser().then(async(response) => {
            if (response) {
                const elem = await response.json();

                const newSteps = {...InitTutorialSteps, ...elem.tutorial_advancement.progress};

                // Migrate old tutorial indices (25-step system) to new (18-step system)
                const totalSteps = TutorialsReferences['main'].length;
                if (elem.tutorial_advancement.enabled && newSteps['main'] >= totalSteps && newSteps['main'] < 25) {
                    newSteps['main'] = migrateStepIndex(newSteps['main']);
                }

                setCurrentTutorialSteps(newSteps);

                if (elem.tutorial_advancement.enabled && newSteps['main'] == 0) {
                    setTutorialModalOpen(true);
                } else {
                    setTutorialEnabled(elem.tutorial_advancement.enabled);
                }
            }
        })
    }, [])

    return (
        <TutorialContext.Provider value={{ currentTutorialSteps, tutorialEnabled, nextTutorialStep, previousTutorialStep, enableTutorials, restartTutorial }}>
          <TutorialModal open={tutorialModalOpen} setOpen={setTutorialModalOpen} setTutorialEnabled={setTutorialEnabled} />
          {children}
        </TutorialContext.Provider>
    )
}


function TutorialStep({ children,  tutorial, identifier, display, position = 'center', marginTop = null, marginLeft = null, marginRight = null, borderRadius = 2, fixedCenter = false }) {

  const { currentTutorialSteps, tutorialEnabled, nextTutorialStep, previousTutorialStep } = useContext(TutorialContext);

  const getTutorialStepByIdentifier = () => {
    return TutorialsReferences[tutorial].filter(elem => elem.key == identifier)[0];
  }

  const isOpen = () => {
    if (tutorialEnabled && currentTutorialSteps[tutorial] < TutorialsReferences[tutorial].length) {
        return TutorialsReferences[tutorial][currentTutorialSteps[tutorial]]['key'] == identifier
    } else {
        return false;
    }
  }

  const open = isOpen();

  const tutorialStepObject = getTutorialStepByIdentifier()

  if (open) {

    return (
        <div style={{ zIndex: 1500 }}>

        <div
            className={cn("relative w-fit h-fit bg-white pointer-events-none")}
            style={{
                display: display,
                zIndex: 2000,
                borderRadius: `${borderRadius * 8}px`,
                border: open ? '1px solid rgba(228, 200, 42, 0.7)' : 'none',
                boxShadow: open ? '0 0 20px rgba(255, 255, 255, 0.7)' : 'none',
            }}
        >
            {children}
        </div>

        {
            open ?
            <div
                className={cn(fixedCenter ? '' : (position == 'center' ? 'flex justify-center' : ''))}
            >
            <div
                className={cn(
                    "z-[2000] w-fit min-w-[300px] h-fit bg-white p-5 rounded-2xl border border-black/15 shadow-lg",
                    fixedCenter ? "fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" : "absolute"
                )}
                style={fixedCenter ? {} : {
                    marginTop: marginTop,
                    marginLeft: marginLeft,
                    marginRight: marginRight,
                }}
                >
                <div
                    className="rounded-[48px] w-[100px] h-[30px] flex flex-row -ml-2 -mb-2"
                >
                    <p className="text-sm font-semibold ml-2 mr-1">
                        Tutorial
                    </p>
                    <span className="material-symbols-outlined text-[20px]">
                        menu_book
                    </span>
                </div>
                <h2 className="text-base font-bold text-center mb-2">
                    {tutorialStepObject.title}
                </h2>
                {tutorialStepObject.content}
                <div
                    className="flex justify-end"
                >
                    <div
                        className="mt-2 rounded-[48px] bg-black h-[30px] w-[85px]"
                    >
                        <button
                            className="rounded-[48px] h-[30px] w-[85px] flex items-center justify-center text-white hover:bg-white/15 transition-colors"
                            onClick={async() => {
                                nextTutorialStep(tutorial);
                            }}
                        >
                            <span className="text-sm">
                                Got It
                            </span>
                            <span className="material-symbols-outlined ml-0.5 h-5 w-5 text-[20px]">
                                done
                            </span>
                        </button>
                    </div>
                </div>
                </div></div> : null
            }

        <div
            className={cn(
                "fixed top-0 left-0 w-screen h-screen bg-black/50 z-[1500]",
                !open && "hidden"
            )}
            onClick={() => {}}
        />
        </div>
    );
    }
    else {
        return children
    }
}

export default TutorialStep;

export { TutorialProvider, TutorialContext, TutorialsReferences }
